import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import {
  disableExternalApi,
  enableExternalApi,
  fetchCompanyExternalApiKeyList,
  setSortType,
  sortCompanyExternalApiKeyList,
} from '../../../../states/containers/setting/companyExternalApiKeySlice'
import { useTranslation } from 'react-i18next'
import { CompanyExternalApiKey, postCompanyExternalApiKeyResponse } from '../../../../states/types/setting'
import ExternalApiKeyItem from './_ExternalApiKeyItem'
import AddExternalApiKeyModal from '../../../components/setting/externalApiKeys/AddExternalApiKeyModal'
import SortBlock from '../../../components/common/SortBlock'
import EditExternalApiKeyModal from '../../../components/setting/externalApiKeys/EditExternalApiKeyModal'
import DetailExternalApiKeyModal from '../../../components/setting/externalApiKeys/DetailExternalApiKeyModal'
import DeleteExternalApiKeyModal from '../../../components/setting/externalApiKeys/DeleteExternalApiKeyModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { nl2br } from '../../../../utils/string'
import { sortType } from '../../../../states/types/common'

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isAddExternalApiKeyModalOpen, setIsAddExternalApiKeyModalOpen] = useState<boolean>(false)
  const [isEditExternalApiKeyModalOpen, setIsEditExternalApiKeyModalOpen] = useState<boolean>(false)
  const [isDetailExternalApiKeyModalOpen, setIsDetailExternalApiKeyModalOpen] = useState<boolean>(false)
  const [isDeleteExternalApiKeyModalOpen, setIsDeleteExternalApiKeyModalOpen] = useState<boolean>(false)
  const [targetCompanyExternalApiKey, setTargetCompanyExternalApiKey] = useState<CompanyExternalApiKey | null>(null)

  const {
    companyExternalApiKey: {
      companyExternalApiKeyList,
      isDisabledExternalApi,
      sortType,
    },
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))


  const handleOpenEditModal = (companyExternalApiKey: CompanyExternalApiKey) => {
    setTargetCompanyExternalApiKey(companyExternalApiKey)
    setIsEditExternalApiKeyModalOpen(true)
  }

  const handleOpenDetailModal = (companyExternalApiKey: CompanyExternalApiKey) => {
    setTargetCompanyExternalApiKey(companyExternalApiKey)
    setIsDetailExternalApiKeyModalOpen(true)
  }

  const handleOpenDeleteModal = (companyExternalApiKey: CompanyExternalApiKey) => {
    setTargetCompanyExternalApiKey(companyExternalApiKey)
    setIsDeleteExternalApiKeyModalOpen(true)
  }

  const handleContactClick = () => {
    // TODO: お問い合わせURLを正しいものに変更
    window.open('https://qasee.jp/contact/', '_blank')
  }

  const handleSortButtonClick = (sortType: sortType) => {
    dispatch(setSortType(sortType))
    dispatch(sortCompanyExternalApiKeyList())
  }

  useEffect(() => {
    if (sortType) {
      dispatch(setSortType(sortType))
      dispatch(sortCompanyExternalApiKeyList())
    }
  }, [sortType])

  useEffect(() => {
    if (!login.accessToken) return
    if (login.external_api === 'none') {
      // プランが定義されていない場合はAPI連携を無効化する
      dispatch(disableExternalApi())
      return
    }

    dispatch(enableExternalApi())
    dispatch(fetchCompanyExternalApiKeyList())
  }, [login.accessToken])

  return (
    <section className="setting_page setting_page_external_api_key">
      <Helmet>
        <title>{`Qasee - 設定 - API連携設定 - ${t('setting.externalApiKey.title')}`}</title>
      </Helmet>

      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        共通設定
        &nbsp;&gt;&nbsp;
        {t('setting.externalApiKey.title')}
        &nbsp;
      </section>

      <section className="page_header">
        <h1>
          {t('setting.externalApiKey.title')}
        </h1>

        { !isDisabledExternalApi && (
          <div className="tools">
            <div className="button_area">
              <button
                type="button"
                className="button"
                onClick={() => setIsAddExternalApiKeyModalOpen(true)}
              >
                {t('setting.externalApiKey.addExternalApiKey')}
              </button>
            </div>
          </div>
        )}
      </section>

      { isDisabledExternalApi ? (
        <section className="page_contents content-area">
          <div className="content-block">
            <div className="block-detail">

              {/* APIキー説明セクション */}
              <section className="api-key-info-container">
                <div className="api-key-info-header">
                  <FontAwesomeIcon icon={['fas', 'info-circle']} className="info-icon" />
                  <h2>{t('setting.externalApiKey.apiKeyInfo.title')}</h2>
                </div>

                <div className="api-key-info-content">
                  <div className="info-section">
                    <h3>
                      {t('setting.externalApiKey.apiKeyInfo.subinfo.0.title')}
                    </h3>
                    <p dangerouslySetInnerHTML={{ __html: nl2br(t('setting.externalApiKey.apiKeyInfo.subinfo.0.description')) }} />
                  </div>

                  <div className="info-section">
                    <h3>
                      {t('setting.externalApiKey.apiKeyInfo.subinfo.1.title')}
                    </h3>
                    <p dangerouslySetInnerHTML={{ __html: nl2br(t('setting.externalApiKey.apiKeyInfo.subinfo.1.description')) }} />
                  </div>

                  <div className="info-section contact-section">
                    <h3>
                      <FontAwesomeIcon icon={['fas', 'headset']} className="section-icon" />
                      {t('setting.externalApiKey.apiKeyInfo.contact.title')}
                    </h3>
                    <p dangerouslySetInnerHTML={{ __html: nl2br(t('setting.externalApiKey.apiKeyInfo.contact.description')) }} />
                    <button
                      className="contact-button"
                      onClick={handleContactClick}
                    >
                      <FontAwesomeIcon icon={['far', 'envelope']} />
                      {t('setting.externalApiKey.apiKeyInfo.contact.button')}
                    </button>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </section>
      ) : (
        <section className="page_contents content-area">
          <div className="content-block">
            <div className="block-detail">

              { !companyExternalApiKeyList || companyExternalApiKeyList?.length == 0 ? (
                <section className="api-key-info-container">
                  {/* APIキー説明セクション */}
                  <div className="api-key-info-header">
                    <FontAwesomeIcon icon={['fas', 'info-circle']} className="info-icon" />
                    <h2>{t('setting.externalApiKey.apiKeyInfo.title')}</h2>
                  </div>

                  <div className="api-key-info-content">
                    <div className="info-section">
                      <h3>
                        {t('setting.externalApiKey.apiKeyInfo.subinfo.0.title')}
                      </h3>
                      <p dangerouslySetInnerHTML={{ __html: nl2br(t('setting.externalApiKey.apiKeyInfo.subinfo.0.description')) }} />
                    </div>

                    <div className="info-section">
                      <h3>
                        {t('setting.externalApiKey.apiKeyInfo.subinfo.1.title')}
                      </h3>
                      <p dangerouslySetInnerHTML={{ __html: nl2br(t('setting.externalApiKey.apiKeyInfo.subinfo.1.description')) }} />
                    </div>

                    <div className="info-section contact-section">
                      <h3>
                        <FontAwesomeIcon icon={['fas', 'plus']} className="section-icon" />
                        {t('setting.externalApiKey.apiKeyInfo.getStarted.title')}
                      </h3>
                      <p dangerouslySetInnerHTML={{ __html: nl2br(t('setting.externalApiKey.apiKeyInfo.getStarted.description')) }} />
                      <button
                        className="contact-button"
                        onClick={() => setIsAddExternalApiKeyModalOpen(true)}
                      >
                        {t('setting.externalApiKey.addExternalApiKey')}
                      </button>
                    </div>
                  </div>
                </section>

              ) : (
                <table className="flex-table member_list_table">
                  <thead>
                    <tr>
                      <th>
                        <SortBlock
                          label={t('model.externalApiKey.operation')}
                          type="operation"
                          currentSortType={sortType!}
                          onSortChange={handleSortButtonClick}
                        />
                      </th>
                      <th>
                        <SortBlock
                          label={t('model.externalApiKey.label')}
                          type="label"
                          currentSortType={sortType!}
                          onSortChange={handleSortButtonClick}
                        />
                      </th>
                      <th>
                        <SortBlock
                          label={t('model.externalApiKey.lastUsedAt')}
                          type="last_used_at"
                          currentSortType={sortType!}
                          onSortChange={handleSortButtonClick}
                        />
                      </th>
                      <th>
                        <SortBlock
                          label={t('model.externalApiKey.createdAt')}
                          type="created_at"
                          currentSortType={sortType!}
                          onSortChange={handleSortButtonClick}
                        />
                      </th>
                      <th>
                        <SortBlock
                          label={t('model.externalApiKey.creator')}
                          type="creator"
                          currentSortType={sortType!}
                          onSortChange={handleSortButtonClick}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    { companyExternalApiKeyList?.map((companyExternalApiKey: CompanyExternalApiKey) => (
                      <ExternalApiKeyItem
                        key={companyExternalApiKey.id}
                        companyExternalApiKey={companyExternalApiKey}
                        openEditModal={handleOpenEditModal}
                        openDetailModal={handleOpenDetailModal}
                        openDeleteModal={handleOpenDeleteModal}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* 作成モーダル */}
          <AddExternalApiKeyModal
            addExternalApiKeyModalState={{
              isOpen: isAddExternalApiKeyModalOpen,
              setIsOpen: setIsAddExternalApiKeyModalOpen,
            }}
            closeCallback={() => {
              setIsAddExternalApiKeyModalOpen(false)
            }}
            submittionCallback={(response: postCompanyExternalApiKeyResponse) => {
              const newCompanyExternalApiKey = response
              setTargetCompanyExternalApiKey(newCompanyExternalApiKey)
              setIsDetailExternalApiKeyModalOpen(true)
            }}
            closeable={true}
          />

          {/* 編集モーダル */}
          {targetCompanyExternalApiKey && (
            <EditExternalApiKeyModal
              companyExternalApiKey={targetCompanyExternalApiKey}
              editExternalApiKeyModalState={{
                isOpen: isEditExternalApiKeyModalOpen,
                setIsOpen: setIsEditExternalApiKeyModalOpen,
              }}
              closeCallback={() => {
                setIsEditExternalApiKeyModalOpen(false)
              }}
              submittionCallback={() => {}}
              closeable={true}
            />
          )}

          {/* 削除モーダル */}
          {targetCompanyExternalApiKey && (
            <DeleteExternalApiKeyModal
              companyExternalApiKey={targetCompanyExternalApiKey}
              deleteExternalApiKeyModalState={{
                isOpen: isDeleteExternalApiKeyModalOpen,
                setIsOpen: setIsDeleteExternalApiKeyModalOpen,
              }}
              closeCallback={() => {
                setIsDeleteExternalApiKeyModalOpen(false)
              }}
            />
          )}

          {/* APIキー表示モーダル　 */}
          {targetCompanyExternalApiKey && (
            <DetailExternalApiKeyModal
              companyExternalApiKey={targetCompanyExternalApiKey}
              detailExternalApiKeyModalState={{
                isOpen: isDetailExternalApiKeyModalOpen,
                setIsOpen: setIsDetailExternalApiKeyModalOpen,
              }}
              closeCallback={() => {
                setIsDetailExternalApiKeyModalOpen(false)
              }}
            />
          )}

        </section>
      )}

    </section>
  )
}

export default Index
